import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

interface ThreadState {
  open: boolean
}
interface ThreadStatesByThreadId {
  open: boolean
  threadStates: Record<string, ThreadState>
}
type RecordThreadStates = Record<string, ThreadStatesByThreadId> // RecordID -> ThreadStatesByThreadId
type ViewThreadStates = Record<string, RecordThreadStates> // ViewID -> RecordThreadStates

export interface CommentsSliceState {
  viewThreadStates: ViewThreadStates
}

const initialState: CommentsSliceState = {
  viewThreadStates: {},
}

export const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    openThread: (
      state,
      action: PayloadAction<{
        viewId: string
        recordId: string
        threadId: string
      }>,
    ) => {
      const { viewId, recordId, threadId } = action.payload
      // eslint-disable-next-line no-param-reassign
      state.viewThreadStates[viewId] ??= {}
      const recordThreadStates = state.viewThreadStates[viewId]
      recordThreadStates[recordId] ??= {
        open: true,
        threadStates: {},
      }
      recordThreadStates[recordId].open = true
      const threadStatesByThreadId = recordThreadStates[recordId]
      const threadState = threadStatesByThreadId.threadStates[threadId]
      if (threadState === undefined) {
        threadStatesByThreadId.threadStates[threadId] = {
          open: true,
        }
        return
      }
      threadState.open = true
    },
    closeThread: (
      state,
      action: PayloadAction<{
        viewId: string
        recordId: string
        threadId: string
      }>,
    ) => {
      const { viewId, recordId, threadId } = action.payload
      // eslint-disable-next-line no-param-reassign
      state.viewThreadStates[viewId] ??= {}
      const recordThreadStates = state.viewThreadStates[viewId]
      if (recordThreadStates[recordId] === undefined) {
        return
      }
      const threadStatesByThreadId = recordThreadStates[recordId].threadStates
      const threadState = threadStatesByThreadId[threadId]
      if (threadState === undefined) {
        return
      }
      threadState.open = false
    },
    openRecordComments: (
      state,
      action: PayloadAction<{ viewId: string; recordId: string; threads: Array<{ id: string; comments: unknown[] }> }>,
    ) => {
      const { viewId, recordId, threads } = action.payload
      // eslint-disable-next-line no-param-reassign
      state.viewThreadStates[viewId] ??= {}
      // eslint-disable-next-line no-param-reassign
      state.viewThreadStates[viewId][recordId] ??= {
        open: true,
        threadStates: {},
      }
      // eslint-disable-next-line no-param-reassign
      state.viewThreadStates[viewId][recordId].open = true
      for (const thread of threads) {
        // eslint-disable-next-line no-param-reassign
        state.viewThreadStates[viewId][recordId].threadStates[thread.id] ??= {
          open: thread.comments.length === 1,
        }
      }
    },
    closeRecordComments: (state, action: PayloadAction<{ viewId: string; recordId: string }>) => {
      const { viewId, recordId } = action.payload
      const recordThreadStates = state.viewThreadStates[viewId]
      if (recordThreadStates === undefined) {
        return
      }
      const threadStatesByThreadId = recordThreadStates[recordId]
      if (threadStatesByThreadId === undefined) {
        return
      }
      threadStatesByThreadId.open = false
    },
  },
  selectors: {
    _selectAllRecordThreadStates: (state: CommentsSliceState) => state.viewThreadStates,
  },
})

export const selectAllRecordThreadStates = createSelector(
  [commentsSlice.selectors._selectAllRecordThreadStates, (_, { viewId }: { viewId: string }) => viewId],
  (viewThreadStates, viewId) => viewThreadStates[viewId] ?? {},
)

export const selectThreadStatesForRecord = createSelector(
  [selectAllRecordThreadStates, (_, { recordId }: { recordId: string | undefined }) => recordId],
  (recordThreadStates, recordId) => {
    if (recordId === undefined) {
      return
    }
    return recordThreadStates[recordId]
  },
)

export const { openThread, closeThread, openRecordComments, closeRecordComments } = commentsSlice.actions
export default commentsSlice.reducer
